import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useTopicLevelIcon from '@/features/Taxonomy/hooks/useTopicLevelIcon';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import {
  STRUCTURED_FILTER_OPERATOR,
  structuredFilterOperatorDic,
} from '@/types/structuredFilterConst';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';

import TopicTypeLabel from '../../../TopicTypeLabel/TopicTypeLabel';
import {
  FilterItemWrapper,
  LabelRow,
  LabelText,
  MainLabelText,
} from './styles';

type Props = {
  topic: Topic2T<TOPIC_2_TYPE>;
  indent?: number;
};

const TopicCard = (props: Props) => {
  const getTopicLvIcon = useTopicLevelIcon();
  const { parseStructuredFilter } = useStructuredFilter();
  const topic = props.topic;
  const offset =
    typeof props.indent === 'undefined' ? topic.level * 54 : props.indent * 54;
  const icon = getTopicLvIcon(topic.level);

  const type = topic.category;

  const [filterQueryFormats, setFilterQueryFormats] = useState<
    StructuredFilterQueryFormatT[]
  >([]);

  useEffect(() => {
    const initFilter = async () => {
      if (type === TOPIC_2_TYPE.RULE) {
        const formats = parseStructuredFilter(topic.filter_rule || {});
        setFilterQueryFormats(formats);
      }
    };
    initFilter();
  }, [JSON.stringify(topic)]);

  const Header = () => {
    return (
      <Flex a="center" j="space-between" width="100%">
        <Flex a="center" g={8}>
          {icon}
          <Typo type="body0" weight="sb">
            {topic.name}
          </Typo>
        </Flex>
        <TopicTypeLabel type={props.topic.category} />
      </Flex>
    );
  };

  if (type === TOPIC_2_TYPE.MANUAL)
    return (
      <Style style={{ marginLeft: offset }}>
        <Header />
      </Style>
    );

  if (type === TOPIC_2_TYPE.RULE) {
    const filter = new SFilter(topic.filter_rule);
    return (
      <Style style={{ marginLeft: offset }}>
        <VFlex g={24} f={1}>
          <Header />
          <VFlex g={12}>
            <LabelRow>
              <MainLabelText>Filter</MainLabelText>
              <Flex padding="4px 0">
                <Typo className="text-gray-600">Match with</Typo>
                <Typo className="text-gray-500">all filter</Typo>
              </Flex>
            </LabelRow>
            <LabelRow>
              <LabelText>Feedback</LabelText>
              <Flex g={4} f={1}>
                {filterQueryFormats.map((filter) => {
                  return (
                    <FilterItemWrapper key={filter.columnKey}>
                      <Typo type="label3">{filter.columnKey}</Typo>
                      <Typo type="label3" className="text-gray-500">
                        {structuredFilterOperatorDic[filter.operator]}
                      </Typo>
                      <Typo type="label3">
                        {filter.filterValues
                          .map((value) => value.filterName)
                          .join(', ')}
                      </Typo>
                    </FilterItemWrapper>
                  );
                })}
              </Flex>
            </LabelRow>
            <LabelRow>
              <LabelText>Include</LabelText>
              <KeywordRuleDisplay
                rule={filter
                  .getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET)
                  ?.find(
                    (filter: StructuredFilterStringNumberValueSet) =>
                      filter.op === STRUCTURED_FILTER_OPERATOR.OR ||
                      filter.op === STRUCTURED_FILTER_OPERATOR.AND,
                  )}
              />
            </LabelRow>
            <LabelRow>
              <LabelText>Exclude</LabelText>
              <KeywordRuleDisplay
                rule={filter
                  .getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET)
                  ?.find(
                    (filter: StructuredFilterStringNumberValueSet) =>
                      filter.op === STRUCTURED_FILTER_OPERATOR.NOR,
                  )}
              />
            </LabelRow>
          </VFlex>
        </VFlex>
      </Style>
    );
  }

  if (type === TOPIC_2_TYPE.AI) {
    const filter = new SFilter(topic.ai_rule.filter);
    return (
      <Style style={{ marginLeft: offset }}>
        <VFlex g={24} f={1}>
          <Header />
          <VFlex g={12}>
            <LabelRow>
              <MainLabelText>Prompt</MainLabelText>
              <Flex f={1}>
                <Typo type="body2" weight="r" className="text-gray-700">
                  {topic.ai_rule.prompt}
                </Typo>
              </Flex>
            </LabelRow>
            <LabelRow>
              <MainLabelText>Filter</MainLabelText>
              <Flex padding="4px 0" g={8}>
                <Typo className="text-gray-600">Match with</Typo>
                <Typo className="text-gray-500">all filter</Typo>
              </Flex>
            </LabelRow>
            <LabelRow>
              <LabelText>Include</LabelText>
              <KeywordRuleDisplay
                rule={filter
                  .getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET)
                  ?.find(
                    (filter: StructuredFilterStringNumberValueSet) =>
                      filter.op === STRUCTURED_FILTER_OPERATOR.OR ||
                      filter.op === STRUCTURED_FILTER_OPERATOR.AND,
                  )}
              />
            </LabelRow>
            <LabelRow>
              <LabelText>Exclude</LabelText>
              <KeywordRuleDisplay
                rule={filter
                  .getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET)
                  ?.find(
                    (filter: StructuredFilterStringNumberValueSet) =>
                      filter.op === STRUCTURED_FILTER_OPERATOR.NOR,
                  )}
              />
            </LabelRow>
          </VFlex>
        </VFlex>
      </Style>
    );
  }
};

const KeywordRuleDisplay = (props: {
  rule?: StructuredFilterStringNumberValueSet;
}) => {
  if (!props.rule || props.rule.values.length === 0)
    return (
      <Flex padding="4px 0">
        <Typo>-</Typo>
      </Flex>
    );
  return (
    <Flex g={16} a="center">
      <Typo className="text-gray-500">
        {structuredFilterUtils.getOperatorTextMap({
          hasMultiValues: props.rule.values.length > 1,
          operator: props.rule.op,
          isMultiValueField: true,
        })}
      </Typo>
      <Flex a="center" g={8}>
        {props.rule.values.map((value) => (
          <FilterItemWrapper key={value}>{value}</FilterItemWrapper>
        ))}
      </Flex>
    </Flex>
  );
};

const Style = styled(Flex)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${(props) => props.theme.radius.card}px;
  padding: 24px;
`;

export default TopicCard;
